import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { ACCOUNTING_LAUNCHED, ACCOUNTING_COMING_LABEL } from '../config/features';
import { useTranslation } from 'react-i18next';
import { 
  House, Users, FileText, Kanban, 
  Bell, Gear, SignOut, Buildings, List, X, Sparkle,
  Lifebuoy, Truck, ArrowCounterClockwise, Receipt, ShoppingCart,
  ChartBar, DownloadSimple, Lock, Crown
} from '@phosphor-icons/react';

const navKeys = [
  { to: '/dashboard', icon: House, key: 'nav.dashboard' },
  { to: '/clients', icon: Users, key: 'nav.clients' },
  { to: '/quotes', icon: FileText, key: 'nav.quotes' },
  { to: '/ai-survey', icon: Sparkle, key: 'nav.aiSurvey' },
  { to: '/pipeline', icon: Kanban, key: 'nav.pipeline' },
  { to: '/follow-ups', icon: Bell, key: 'nav.followUp' },
  // Assistente IA: hidden from the menu (its features live in quotes and clients); /assistente still works
  { to: '/tickets', icon: Lifebuoy, key: 'nav.support' },
  { to: '/settings', icon: Gear, key: 'nav.settings' },
];

const accountingNavKeys = [
  { to: '/contabilita', icon: ChartBar, key: 'nav.accountingDashboard' },
  { to: '/ddt', icon: Truck, key: 'nav.ddt' },
  { to: '/proforma', icon: FileText, key: 'nav.proforma' },
  { to: '/fatture', icon: Receipt, key: 'nav.invoices' },
  { to: '/note-credito', icon: ArrowCounterClockwise, key: 'nav.creditNotes' },
  { to: '/acquisti', icon: ShoppingCart, key: 'nav.purchases' },
  { to: '/fatture-passive', icon: DownloadSimple, key: 'nav.passiveInvoices' },
];

export function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const { plan, planName, inTrial, trialDaysLeft, canAccess } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const canAccessAccounting = canAccess('accounting');

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-[#E2E8F0] flex flex-col
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0 lg:static lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-[#E2E8F0] shrink-0">
          <button
            onClick={() => { onClose(); navigate('/dashboard'); }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="logo-home-button"
          >
            <Buildings size={28} weight="duotone" className="text-[#0033A0]" />
            <span className="font-outfit text-xl font-black text-[#0033A0]">Infismart</span>
          </button>
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-[#4A5568] hover:text-[#0A0F1A]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Trial / Plan badge */}
        {(inTrial || plan) && (
          <div className={`mx-3 mt-3 px-3 py-2 rounded-lg text-xs ${inTrial ? 'bg-amber-50 border border-amber-200' : 'bg-[#0033A0]/5 border border-[#0033A0]/10'}`} data-testid="plan-badge">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Crown size={14} className={inTrial ? 'text-amber-600' : 'text-[#0033A0]'} weight="fill" />
                <span className={`font-semibold ${inTrial ? 'text-amber-800' : 'text-[#0033A0]'}`}>{planName}</span>
              </div>
            </div>
            {inTrial && (
              <p className="text-amber-700 mt-1">
                Prova gratuita: {trialDaysLeft} giorn{trialDaysLeft === 1 ? 'o' : 'i'} rimanent{trialDaysLeft === 1 ? 'e' : 'i'}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navKeys.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => `
                flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium
                transition-colors duration-150
                ${isActive 
                  ? 'bg-[#0033A0] text-white' 
                  : 'text-[#4A5568] hover:bg-[#F4F6F8] hover:text-[#0A0F1A]'
                }
              `}
              data-testid={`nav-${item.key.split('.')[1]}`}
            >
              <item.icon size={20} weight="duotone" />
              {t(item.key)}
            </NavLink>
          ))}

          {/* Accounting Section (hidden until the module is launched) */}
          {ACCOUNTING_LAUNCHED && (
          <div className="pt-3 mt-3 border-t border-[#E2E8F0]">
            <div className="flex items-center justify-between px-3 mb-2">
              <p className="text-[10px] font-bold text-[#4A5568] uppercase tracking-wider">{t('nav.accounting')}</p>
              {!canAccessAccounting && (
                <span className="flex items-center gap-1 text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-testid="accounting-locked-badge">
                  <Lock size={10} weight="bold" /> PRO
                </span>
              )}
            </div>
            {canAccessAccounting ? (
              accountingNavKeys.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium
                    transition-colors duration-150
                    ${isActive 
                      ? 'bg-[#0033A0] text-white' 
                      : 'text-[#4A5568] hover:bg-[#F4F6F8] hover:text-[#0A0F1A]'
                    }
                  `}
                  data-testid={`nav-${item.key.split('.')[1]}`}
                >
                  <item.icon size={20} weight="duotone" />
                  {t(item.key)}
                </NavLink>
              ))
            ) : (
              <button
                onClick={() => { onClose(); navigate('/settings'); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-amber-700 bg-amber-50/50 hover:bg-amber-50 border border-amber-100 transition-colors"
                data-testid="upgrade-accounting-btn"
              >
                <Lock size={18} className="text-amber-500" />
                <div className="text-left">
                  <p className="font-medium text-xs">Modulo Contabilità</p>
                  <p className="text-[10px] text-amber-600">Passa a Gestionale Completo</p>
                </div>
              </button>
            )}
          </div>
          )}

          {/* Accounting not launched yet: shown greyed out and not clickable, with its launch date */}
          {!ACCOUNTING_LAUNCHED && (
            <div className="pt-3 mt-3 border-t border-[#E2E8F0]" aria-disabled="true" data-testid="accounting-coming-soon">
              <div className="px-3 mb-2">
                <p className="text-[10px] font-bold text-[#A0AEC0] uppercase tracking-wider">{t('nav.accounting')}</p>
                <p className="text-[10px] font-medium text-[#0033A0]/70 mt-0.5" data-testid="accounting-coming-label">{ACCOUNTING_COMING_LABEL}</p>
              </div>
              <div className="opacity-40 select-none cursor-not-allowed" title={ACCOUNTING_COMING_LABEL}>
                {accountingNavKeys.map((item) => (
                  <div key={item.to} className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-[#4A5568]">
                    <item.icon size={20} weight="duotone" />
                    {t(item.key)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings now lives inside main nav (moved below Support) */}
        </nav>

        {/* User section - always visible at bottom */}
        <div className="border-t border-[#E2E8F0] p-4 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#0033A0] flex items-center justify-center text-white font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0A0F1A] truncate">{user?.name}</p>
              <p className="text-xs text-[#4A5568] truncate">{user?.company_name || user?.email}</p>
            </div>
          </div>
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-sm transition-colors"
            data-testid="logout-button"
          >
            <SignOut size={18} />
            {t('nav.logout')}
          </button>
        </div>
      </aside>
    </>
  );
}

export function Header({ onMenuClick }) {
  const { user } = useAuth();
  const { t } = useTranslation();

  return (
    <header className="h-16 bg-white border-b border-[#E2E8F0] px-4 flex items-center justify-between lg:justify-end">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 text-[#4A5568] hover:text-[#0A0F1A]"
        data-testid="mobile-menu-button"
      >
        <List size={24} />
      </button>

      <div className="flex items-center gap-4">
        <span className="text-sm text-[#4A5568] hidden sm:block">
          {user?.company_name || t('nav.yourCompany')}
        </span>
        <div className="w-8 h-8 rounded-full bg-[#0033A0] flex items-center justify-center text-white text-sm font-medium">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
      </div>
    </header>
  );
}
